import { useState, useCallback, useRef, useEffect } from "react";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function rndInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function rnd(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ── UUID ──────────────────────────────────────────────────────────────────────
function genUUIDv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
function genUUIDv1Like() {
  const now = Date.now();
  const hi  = Math.floor(now / 0x100000000);
  const lo  = now >>> 0;
  const hiHex = hi.toString(16).padStart(8, "0");
  const loHex = lo.toString(16).padStart(8, "0");
  const r = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4,"0");
  return `${loHex}-${hiHex.slice(4)}-1${hiHex.slice(1,4)}-${((Math.random()*0x3fff+0x8000)|0).toString(16)}-${r()}${r()}${r()}`;
}
function genUUIDnil() { return "00000000-0000-0000-0000-000000000000"; }

// ── PASSWORD ──────────────────────────────────────────────────────────────────
const LOWER  = "abcdefghijklmnopqrstuvwxyz";
const UPPER  = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS= "!@#$%^&*()-_=+[]{}|;:,.<>?";
const AMBIGUOUS = /[0OIl1]/g;

function genPassword({ length, useLower, useUpper, useDigits, useSymbols, noAmbiguous }) {
  let pool = "";
  if (useLower)   pool += LOWER;
  if (useUpper)   pool += UPPER;
  if (useDigits)  pool += DIGITS;
  if (useSymbols) pool += SYMBOLS;
  if (noAmbiguous) pool = pool.replace(AMBIGUOUS, "");
  if (!pool) return "";
  let pwd = "";
  // Guarantee at least one of each selected type
  if (useLower)   pwd += rnd(noAmbiguous ? LOWER.replace(AMBIGUOUS,"") : LOWER);
  if (useUpper)   pwd += rnd(noAmbiguous ? UPPER.replace(AMBIGUOUS,"") : UPPER);
  if (useDigits)  pwd += rnd(noAmbiguous ? DIGITS.replace(AMBIGUOUS,"") : DIGITS);
  if (useSymbols) pwd += rnd(SYMBOLS);
  while (pwd.length < length) pwd += pool[rndInt(0, pool.length - 1)];
  return pwd.split("").sort(() => Math.random() - 0.5).join("").slice(0, length);
}

function passwordStrength(pwd) {
  if (!pwd) return { label:"", color:"", score:0 };
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (pwd.length >= 16) score++;
  if (/[a-z]/.test(pwd)) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  if (score <= 2) return { label:"Weak",    color:"#ef4444", score };
  if (score <= 4) return { label:"Fair",    color:"#f59e0b", score };
  if (score <= 5) return { label:"Good",    color:"#3b8ef8", score };
  return              { label:"Strong",  color:"#22c55e", score };
}

// ── LOREM IPSUM ───────────────────────────────────────────────────────────────
const LOREM_WORDS = ["lorem","ipsum","dolor","sit","amet","consectetur","adipiscing","elit","sed","do","eiusmod","tempor","incididunt","ut","labore","et","dolore","magna","aliqua","enim","ad","minim","veniam","quis","nostrud","exercitation","ullamco","laboris","nisi","aliquip","ex","ea","commodo","consequat","duis","aute","irure","in","reprehenderit","voluptate","velit","esse","cillum","fugiat","nulla","pariatur","excepteur","sint","occaecat","cupidatat","non","proident","sunt","culpa","qui","officia","deserunt","mollit","anim","id","est","laborum","praesent","viverra","justo","vestibulum","neque","purus","condimentum","habitant","morbi","tristique","senectus","netus","malesuada","fames","turpis","egestas","volutpat","ac","adipiscing","vitae","proin","nibh","nisl","condimentum","id","venenatis","a","condimentum","vitae","sapien","pellentesque","habitant","morbi","tristique","senectus"];

function genLoremWord() { return rnd(LOREM_WORDS); }
function genLoremSentence(wordCount = rndInt(8,18)) {
  const words = Array.from({ length: wordCount }, () => genLoremWord());
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(" ") + ".";
}
function genLoremParagraph(sentenceCount = rndInt(4,8)) {
  return Array.from({ length: sentenceCount }, () => genLoremSentence()).join(" ");
}
function genLorem({ type, count, startWithLorem }) {
  let output = "";
  if (type === "words") {
    const words = Array.from({ length: count }, () => genLoremWord());
    if (startWithLorem && count >= 2) { words[0] = "lorem"; words[1] = "ipsum"; }
    output = words.join(" ");
  } else if (type === "sentences") {
    output = Array.from({ length: count }, () => genLoremSentence()).join(" ");
    if (startWithLorem) output = "Lorem ipsum dolor sit amet. " + output;
  } else {
    output = Array.from({ length: count }, () => genLoremParagraph()).join("\n\n");
    if (startWithLorem) output = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.\n\n" + output;
  }
  return output;
}

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, t, label = "⎘ Copy" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn-sm" onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })}
      style={{ borderColor: copied ? t.statusOk : t.border, color: copied ? t.statusOk : t.textMuted }}>
      {copied ? "✓ Copied" : label}
    </button>
  );
}

// ─── UUID GENERATOR ───────────────────────────────────────────────────────────
function UUIDGenerator({ t }) {
  const [version, setVersion]   = useState("v4");
  const [count, setCount]       = useState(10);
  const [uuids, setUUIDs]       = useState([]);
  const [uppercase, setUppercase] = useState(false);
  const [braces, setBraces]     = useState(false);

  const generate = useCallback(() => {
    const gen = version === "v4" ? genUUIDv4 : version === "v1" ? genUUIDv1Like : genUUIDnil;
    const list = Array.from({ length: Math.min(count, 100) }, () => {
      let id = gen();
      if (uppercase) id = id.toUpperCase();
      if (braces) id = `{${id}}`;
      return id;
    });
    setUUIDs(list);
  }, [version, count, uppercase, braces]);

  useEffect(() => { generate(); }, [generate]);

  const allText = uuids.join("\n");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Version</span>
          <select value={version} onChange={e => setVersion(e.target.value)}
            style={{ background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
            <option value="v4">v4 (random)</option>
            <option value="v1">v1 (time-based)</option>
            <option value="nil">nil (zeros)</option>
          </select>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Count</span>
          <input type="number" min={1} max={100} value={count} onChange={e => setCount(Math.min(100, Math.max(1, +e.target.value || 1)))}
            style={{ width:60, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
        </div>
        {[{label:"UPPERCASE",val:uppercase,fn:()=>setUppercase(v=>!v)},{label:"With braces {}",val:braces,fn:()=>setBraces(v=>!v)}].map(({label,val,fn})=>(
          <div key={label} onClick={fn} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
            <div className={`toggle ${val?"on":""}`}><div className="toggle-thumb"/></div>
            <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>{label}</span>
          </div>
        ))}
        <button className="btn-primary" onClick={generate} style={{ padding:"7px 18px", fontSize:12 }}>↻ Regenerate</button>
      </div>

      <div className="panel active-panel">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{uuids.length} UUIDs Generated</span>
          <div style={{ display:"flex", gap:6 }}>
            <CopyBtn text={allText} t={t} label="⎘ Copy All" />
            <button className="btn-sm" onClick={() => { const b = new Blob([allText], {type:"text/plain"}); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href=u; a.download="uuids.txt"; a.click(); URL.revokeObjectURL(u); }}>↓ Download</button>
          </div>
        </div>
        <div style={{ maxHeight:400, overflowY:"auto", scrollbarWidth:"thin" }}>
          {uuids.map((id, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 14px", borderBottom:`1px solid ${t.border}`, fontFamily:"JetBrains Mono", fontSize:13, color:t.textSecondary }}>
              <span style={{ color:t.textMuted, fontSize:10, minWidth:28 }}>{i+1}</span>
              <span style={{ flex:1 }}>{id}</span>
              <CopyBtn text={id} t={t} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PASSWORD GENERATOR ───────────────────────────────────────────────────────
function PasswordGenerator({ t }) {
  const [opts, setOpts] = useState({ length:16, useLower:true, useUpper:true, useDigits:true, useSymbols:true, noAmbiguous:false });
  const [count, setCount] = useState(5);
  const [passwords, setPasswords] = useState([]);

  const generate = useCallback(() => {
    setPasswords(Array.from({ length: Math.min(count, 50) }, () => genPassword(opts)));
  }, [opts, count]);

  useEffect(() => { generate(); }, [generate]);

  const setOpt = (k, v) => setOpts(prev => ({ ...prev, [k]: v }));
  const strength = passwords[0] ? passwordStrength(passwords[0]) : null;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Options */}
      <div className="panel">
        <div style={{ padding:"10px 14px", borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent, background:t.bgPanelHead }}>Settings</div>
        <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:12 }}>
          {/* Length slider */}
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:12, color:t.textLabel, fontFamily:"JetBrains Mono", minWidth:60 }}>Length</span>
            <input type="range" min={6} max={64} value={opts.length} onChange={e => setOpt("length", +e.target.value)}
              style={{ flex:1 }} />
            <span style={{ fontSize:14, fontWeight:700, color:t.textAccent, fontFamily:"JetBrains Mono", minWidth:28, textAlign:"right" }}>{opts.length}</span>
          </div>
          {/* Character sets */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {[
              { key:"useLower",    label:"Lowercase (a-z)" },
              { key:"useUpper",    label:"Uppercase (A-Z)" },
              { key:"useDigits",   label:"Numbers (0-9)" },
              { key:"useSymbols",  label:"Symbols (!@#…)" },
              { key:"noAmbiguous", label:"Exclude ambiguous (0O1l)" },
            ].map(({ key, label }) => (
              <div key={key} onClick={() => setOpt(key, !opts[key])}
                style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
                <div className={`toggle ${opts[key]?"on":""}`}><div className="toggle-thumb"/></div>
                <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>{label}</span>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:12, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Generate</span>
            {[1,5,10,20].map(n => (
              <button key={n} className="btn-sm" onClick={() => setCount(n)}
                style={{ borderColor: count===n ? t.borderActive : t.border, color: count===n ? t.textAccent : t.textMuted, background: count===n ? t.bgChipActive : "none" }}>{n}</button>
            ))}
            <button className="btn-primary" onClick={generate} style={{ padding:"5px 16px", fontSize:12 }}>↻ Regenerate</button>
          </div>
        </div>
      </div>

      {/* Strength indicator */}
      {strength && passwords[0] && (
        <div style={{ display:"flex", alignItems:"center", gap:12, background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"10px 16px" }}>
          <span style={{ fontSize:12, color:t.textMuted }}>Strength:</span>
          <div style={{ flex:1, height:6, background:t.bgToggle, borderRadius:3, overflow:"hidden" }}>
            <div style={{ width:`${(strength.score/7)*100}%`, height:"100%", background:strength.color, borderRadius:3, transition:"width 0.3s" }} />
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:strength.color, minWidth:50 }}>{strength.label}</span>
        </div>
      )}

      {/* Results */}
      <div className="panel active-panel">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{passwords.length} Passwords</span>
          <CopyBtn text={passwords.join("\n")} t={t} label="⎘ Copy All" />
        </div>
        {passwords.map((pwd, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 14px", borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:13, color:t.textPrimary, letterSpacing:"0.05em", flex:1, wordBreak:"break-all" }}>{pwd}</span>
            <CopyBtn text={pwd} t={t} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LOREM IPSUM GENERATOR ────────────────────────────────────────────────────
function LoremIpsumGenerator({ t }) {
  const [type, setType]         = useState("paragraphs");
  const [count, setCount]       = useState(3);
  const [startWithLorem, setStart] = useState(true);
  const [output, setOutput]     = useState("");

  const generate = useCallback(() => {
    setOutput(genLorem({ type, count: Math.min(count, type==="words"?1000:type==="sentences"?50:20), startWithLorem }));
  }, [type, count, startWithLorem]);

  useEffect(() => { generate(); }, [generate]);

  const wordCount = output.trim() ? output.trim().split(/\s+/).length : 0;
  const charCount = output.length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Type</span>
          <select value={type} onChange={e => setType(e.target.value)}
            style={{ background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
            <option value="words">Words</option>
            <option value="sentences">Sentences</option>
            <option value="paragraphs">Paragraphs</option>
          </select>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Count</span>
          <input type="number" min={1} max={type==="words"?1000:type==="sentences"?50:20} value={count}
            onChange={e => setCount(Math.max(1, +e.target.value || 1))}
            style={{ width:60, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
        </div>
        <div onClick={() => setStart(v=>!v)} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
          <div className={`toggle ${startWithLorem?"on":""}`}><div className="toggle-thumb"/></div>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Start with "Lorem ipsum"</span>
        </div>
        <button className="btn-primary" onClick={generate} style={{ padding:"7px 18px", fontSize:12 }}>↻ Regenerate</button>
      </div>

      <div className="panel active-panel">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            {wordCount.toLocaleString()} words · {charCount.toLocaleString()} chars
          </span>
          <CopyBtn text={output} t={t} />
        </div>
        <textarea readOnly value={output}
          style={{ width:"100%", height:340, background:"transparent", border:"none", outline:"none", padding:"14px", color:t.textSecondary, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:13, lineHeight:1.8, resize:"none" }} />
      </div>
    </div>
  );
}

// ─── QR CODE GENERATOR ────────────────────────────────────────────────────────
// Pure JS QR code using a CDN-loaded library
function QRCodeGenerator({ t }) {
  const [input, setInput]     = useState("https://minidevtoolkit.com");
  const [size, setSize]       = useState(256);
  const [ecLevel, setEcLevel] = useState("M");
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const canvasRef = useRef(null);
  const [error, setError]     = useState(null);
  const [generated, setGenerated] = useState(false);

  const generate = useCallback(async () => {
    if (!input.trim()) return;
    setError(null);
    try {
      // Load QRCode library from CDN
      if (!window.QRCode) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      const container = document.getElementById("qr-container");
      if (container) container.innerHTML = "";
      // eslint-disable-next-line no-new
      new window.QRCode(container, {
        text: input.trim(),
        width: size,
        height: size,
        colorDark: fgColor,
        colorLight: bgColor,
        correctLevel: window.QRCode.CorrectLevel[ecLevel],
      });
      setGenerated(true);
    } catch(e) {
      setError("Could not generate QR code — text may be too long");
    }
  }, [input, size, ecLevel, fgColor, bgColor]);

  useEffect(() => { generate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadQR = () => {
    const container = document.getElementById("qr-container");
    if (!container) return;
    const canvas = container.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a"); a.href = url; a.download = "qrcode.png"; a.click();
    }
  };

  const SIZES = [128, 256, 512];
  const EC_LEVELS = [
    { id:"L", label:"L — Low (7%)" },
    { id:"M", label:"M — Medium (15%)" },
    { id:"Q", label:"Q — Quartile (25%)" },
    { id:"H", label:"H — High (30%)" },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div className="grid-2" style={{ alignItems:"start" }}>
        {/* Controls */}
        <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
          <div className="panel">
            <div style={{ padding:"8px 12px", borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent, background:t.bgPanelHead }}>Content</div>
            <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Enter URL, text, email, phone number…" spellCheck={false}
              style={{ width:"100%", height:100, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:13, lineHeight:1.6, resize:"none" }} />
            <div style={{ padding:"8px 14px", borderTop:`1px solid ${t.border}` }}>
              <button className="btn-primary" onClick={generate} disabled={!input.trim()} style={{ width:"100%", justifyContent:"center", padding:"9px" }}>⚡ Generate QR Code</button>
            </div>
          </div>

          <div className="panel">
            <div style={{ padding:"8px 12px", borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent, background:t.bgPanelHead }}>Options</div>
            <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:12 }}>
              {/* Size */}
              <div>
                <div style={{ fontSize:11, color:t.textMuted, marginBottom:6, fontFamily:"JetBrains Mono" }}>Size (px)</div>
                <div style={{ display:"flex", gap:6 }}>
                  {SIZES.map(s => (
                    <button key={s} className="btn-sm" onClick={() => setSize(s)}
                      style={{ borderColor: size===s?t.borderActive:t.border, color:size===s?t.textAccent:t.textMuted, background:size===s?t.bgChipActive:"none", padding:"4px 16px" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {/* Error correction */}
              <div>
                <div style={{ fontSize:11, color:t.textMuted, marginBottom:6, fontFamily:"JetBrains Mono" }}>Error Correction</div>
                <select value={ecLevel} onChange={e => setEcLevel(e.target.value)}
                  style={{ width:"100%", background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"6px 10px", fontSize:12, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
                  {EC_LEVELS.map(ec => <option key={ec.id} value={ec.id}>{ec.label}</option>)}
                </select>
              </div>
              {/* Colors */}
              <div style={{ display:"flex", gap:12 }}>
                {[{label:"Foreground",val:fgColor,set:setFgColor},{label:"Background",val:bgColor,set:setBgColor}].map(({label,val,set})=>(
                  <div key={label} style={{ flex:1 }}>
                    <div style={{ fontSize:11, color:t.textMuted, marginBottom:6, fontFamily:"JetBrains Mono" }}>{label}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"5px 10px" }}>
                      <input type="color" value={val} onChange={e=>set(e.target.value)} style={{ width:24, height:24, border:"none", background:"none", cursor:"pointer", padding:0 }} />
                      <span style={{ fontSize:11, fontFamily:"JetBrains Mono", color:t.textSecondary }}>{val}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* QR Preview */}
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>QR Code Preview</span>
            {generated && <button className="btn-sm" onClick={downloadQR}>↓ Download PNG</button>}
          </div>
          <div style={{ padding:"24px", display:"flex", justifyContent:"center", alignItems:"center", minHeight:300, background:t.bgInput }}>
            {error ? (
              <div style={{ color:t.statusErr, fontSize:13, textAlign:"center" }}>{error}</div>
            ) : (
              <div id="qr-container" ref={canvasRef} style={{ lineHeight:0 }} />
            )}
          </div>
          {generated && (
            <div style={{ padding:"8px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color:t.statusOk }}>
              ✓ {size}×{size}px · EC level {ecLevel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TOOLS = [
  { id:"uuid",     label:"UUID Generator",       icon:"🆔", desc:"Generate UUID v1/v4/nil" },
  { id:"password", label:"Password Generator",   icon:"🔑", desc:"Secure random passwords" },
  { id:"lorem",    label:"Lorem Ipsum",          icon:"📄", desc:"Placeholder text generator" },
  { id:"qr",       label:"QR Code",              icon:"⬛", desc:"Generate scannable QR codes" },
];

export default function GeneratorsToolkit({ t }) {
  const [active, setActive] = useState("uuid");

  const components = {
    uuid:     <UUIDGenerator t={t} />,
    password: <PasswordGenerator t={t} />,
    lorem:    <LoremIpsumGenerator t={t} />,
    qr:       <QRCodeGenerator t={t} />,
  };

  return (
    <div>
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>⚡ Generators</h2>
            <p>UUID, secure passwords, Lorem Ipsum placeholder text, and QR codes — all generated instantly in your browser.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">4</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">0</div><div className="tool-stat-lbl">server calls</div></div>
          </div>
        </div>
      </div>

      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap" }}>
        {TOOLS.map(tool => {
          const active2 = active === tool.id;
          return (
            <button key={tool.id} onClick={() => setActive(tool.id)}
              className={`pill-tab ${active2 ? "active" : ""}`}>
              <span>{tool.icon}</span>{tool.label}
            </button>
          );
        })}
      </div>

      <div className="tool-main">
        {components[active]}
      </div>

      <article className="tool-seo-section" aria-label="About Generators">
        <h2>About Generators</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>UUID v4 uses <code>Math.random()</code> with RFC 4122 compliant bit masking.</li>
              <li>Password generator uses a cryptographically seeded character pool with guaranteed character-class inclusion.</li>
              <li>Lorem Ipsum generates placeholder text from a classic Latin corpus.</li>
              <li>QR Code renders via the qrcode.js library loaded from CDN on first use.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Generate database record IDs or correlation IDs.</li>
              <li>Create strong passwords for new accounts.</li>
              <li>Fill UI mockups with realistic placeholder text.</li>
              <li>Create QR codes for URLs, contact info, or Wi-Fi credentials.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All generation happens in your browser — nothing is sent to a server.</li>
              <li>QR Code generation uses a CDN library but your QR data stays local.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>Is UUID v4 truly random?</summary><p>UUID v4 uses Math.random() seeded by the browser's entropy source, making collisions astronomically unlikely (1 in 5.3×10³⁶). For cryptographic use cases, use a dedicated CSPRNG. For database IDs and correlation tokens, UUID v4 is perfectly sufficient.</p></details>
        <details><summary>How secure is the password generator?</summary><p>The password generator uses a shuffled pool from your selected character sets and guarantees at least one character from each enabled set. The strength meter scores length and character variety. For maximum security, enable all character sets and use 16+ characters.</p></details>
        <details><summary>Can I generate multiple UUIDs at once?</summary><p>Yes. Set the count slider to up to 100 and click Generate. All UUIDs are generated client-side instantly. You can copy individual UUIDs or download all as a text file.</p></details>
        <details><summary>Does the password generator use a secure random source?</summary><p>The password generator uses a shuffled character pool with Math.random(). For cryptographic key material, use the browser's Web Crypto API directly. For application passwords, login credentials, and API tokens, the generator's output is more than adequate.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>UUID Generator.net</strong> — miniDevToolkit bundles UUID generation alongside a QR code generator, password generator, and Lorem Ipsum in one tab, rather than requiring separate bookmarks. Compared to <strong>random.org</strong> — miniDevToolkit generates passwords and UUIDs entirely offline in your browser (random.org makes server requests). Compared to <strong>uuidgenerator.net</strong> — miniDevToolkit adds v1/v4/nil support, UPPERCASE/braces options, and bulk download in the same free, no-sign-up interface.</p>
      </article>
    </div>
  );
}